import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ObservationContext } from 'apps/sigma2-ui/src/state/observation';
import { UserCallbacks } from 'apps/sigma2-ui/src/state/user-types';
import { UserContext } from 'apps/sigma2-ui/src/state/user-types';
import { TopLevelAnalysisCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/TopLevelAnalysisDefinition';
import { CustomViz } from 'apps/sigma2-ui/src/components/custom-viz/CustomViz';
import { useUserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-authenticated-api-call';
import { useParams } from 'react-router';
import { DashboardRecordDto } from 'libs/end-to-end/src/dashboard';
import { DashboardWithChartsAndGuestInfo } from 'libs/end-to-end/src/dashboard';
import { DraftDashboard } from 'libs/end-to-end/src/dashboard';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useCustomVizAutoload } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { TopLevelAnalysisResult } from 'libs/end-to-end/src/top-level-analysis';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { CoverAllLoad } from 'apps/sigma2-ui/src/components/CoverAllLoad';
import { FilterStoreRenderer } from 'apps/sigma2-ui/src/filters-v2/components/FilterStoreRenderer';
import { DashboardDetailsView } from 'apps/sigma2-ui/src/pages/Dashboards/DashboardDetailsPage';
import { convertDashboardRecordToDraft } from 'libs/end-to-end/src/dashboards.utils';
import { updateChartInDraft } from 'libs/end-to-end/src/dashboards.utils';
import { updateGlobalFiltersInDraft } from 'libs/end-to-end/src/dashboards.utils';
import { DashboardsContext } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { UpdateDashboardDraftGlobalFiltersFn } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { EmbedModeContext } from 'apps/sigma2-ui/src/components/EmbedModeContext';
import { UpdateDraftChartFn } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';

export const EmbedDashboardPage = () => {
  const { dashboardId } = useParams();
  const [dashboardWithCharts, setDashboardWithCharts] =
    useState<DashboardWithChartsAndGuestInfo | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const onUrlCallback = useCallback(
    (originalUrl: string) => {
      const parsedUrl = new URL(originalUrl, window.location.origin);
      if (!parsedUrl.pathname.startsWith('/api/v2/custom-viz/')) {
        return originalUrl;
      }
      parsedUrl.pathname += '/embed-dashboard/' + dashboardId;
      return parsedUrl.toString();
    },
    [dashboardId],
  );

  const userAuthenticatedApiCall = useUserAuthenticatedApiCall(onUrlCallback);

  const isInitialized = useRef(false);

  useAsyncEffect(
    async (isAlive) => {
      if (!isAlive()) {
        return;
      }

      if (isInitialized.current || !dashboardId) {
        return;
      }

      isInitialized.current = true;

      setLoading(true);

      const fetchDashboardResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/embed-dashboard/${dashboardId}`,
        responseSchema: DashboardWithChartsAndGuestInfo,
        requestOptions: {
          method: 'GET',
        },
      });

      if (!isAlive() || !fetchDashboardResult) {
        return;
      }

      if (fetchDashboardResult.resultType === 'err') {
        setError(true);
        setLoading(false);
        return;
      }

      setDashboardWithCharts(fetchDashboardResult.value);
    },
    [dashboardId, userAuthenticatedApiCall],
  );

  if (!dashboardId) {
    return <></>;
  }

  // Use a user modeling what the API would normally return for this user.
  const user: UserContext = {
    myUserStatus: dashboardWithCharts ? 'logged_in' : 'logging_in',
    logoutLink: null,
    myUser: dashboardWithCharts ? dashboardWithCharts.guestInfo.myUser : null,
    isAuthenticated: !!dashboardWithCharts,
    featureFlags: dashboardWithCharts
      ? dashboardWithCharts.guestInfo.featureFlags
      : null,
    login: () => {},
    loginWithSSO: () => {},
    logout: () => {},
    startLoggingOut: () => {},
    userAuthenticatedApiCall,
    loadUser: () => {},
    loadFeatureFlags: () => {},
    callbacks: new UserCallbacks(),
  };

  return (
    <UserContext.Provider value={user}>
      <FilterStoreRenderer>
        <div
          className={
            'bg-[#FDFDFF] relative w-screen h-screen overflow-y-scroll'
          }
        >
          {loading && <CoverAllLoad />}
          <div className={'p-4'}>
            {error && <div>Error loading dashboard.</div>}
            {dashboardWithCharts && (
              <CustomViz
                observation={{
                  type: 'db-revision',
                  databaseId: dashboardWithCharts.guestInfo.databaseId,
                  databaseRevisionId:
                    dashboardWithCharts.guestInfo.databaseRevisionId,
                }}
                organizationId={dashboardWithCharts.guestInfo.organizationId}
                customVizDefinition={TopLevelAnalysisCustomVizDefinition}
                userAuthenticatedApiCall={userAuthenticatedApiCall}
              >
                <EmbedDashboardPageTopLevelContainer
                  databaseRevisionId={
                    dashboardWithCharts.guestInfo.databaseRevisionId
                  }
                  organizationId={dashboardWithCharts.guestInfo.organizationId}
                  databaseId={dashboardWithCharts.guestInfo.databaseId}
                  dashboardWithCharts={dashboardWithCharts}
                  loading={loading}
                  error={error}
                  setLoading={setLoading}
                  setError={setError}
                />
              </CustomViz>
            )}
          </div>
        </div>
      </FilterStoreRenderer>
    </UserContext.Provider>
  );
};

const EmbedDashboardPageTopLevelContainer = ({
  databaseId,
  databaseRevisionId,
  organizationId,
  dashboardWithCharts,
  loading,
  error,
  setLoading,
  setError,
}: {
  databaseId: string;
  databaseRevisionId: string;
  organizationId: string;
  dashboardWithCharts: DashboardWithChartsAndGuestInfo;
  loading: boolean;
  error: boolean;
  setLoading: (loading: boolean) => void;
  setError: (error: boolean) => void;
}) => {
  const context = useCustomViz();
  const doViz = context.doViz;
  const status = context.status;

  const onAnalyze = useCallback(() => {
    doViz({
      customVizId: 'topLevelAnalysis',
      observationLocationType: {
        type: 'db-revision',
        databaseId,
        databaseRevisionId,
      },
      organizationId,
      inputs: {
        dropdowns: {},
        extras: {},
      },
    });
  }, [doViz, databaseId, databaseRevisionId, organizationId]);

  const { data, hasFailed, isLoading } = useCustomVizAutoload({
    canAnalyse: true,
    onAnalyze,
    context,
    ResultZodType: TopLevelAnalysisResult,
  });

  useEffect(() => {
    if (hasFailed && !error) {
      setError(true);
    }
  }, [hasFailed, error, setError]);

  useEffect(() => {
    if (data && !isLoading && loading) {
      setLoading(false);
    }
  }, [data, isLoading, loading, setLoading]);

  // Use an observation modeling what the API would normally return for this user.
  const observation: ObservationContext = useMemo(() => {
    return {
      selectedOrganizationId: organizationId,
      myObservation:
        Boolean(data) && dashboardWithCharts.guestInfo.myObservation
          ? dashboardWithCharts.guestInfo.myObservation
          : null,
      removeTreatment: () => {},
      toggleTreatment: () => {},
      updateTreatments: () => {},
      failedToLoadObservation: false,
      staleResultShowing: !!data && status === 'loading-viz-with-stale-result',
    };
  }, [
    dashboardWithCharts.guestInfo.myObservation,
    data,
    organizationId,
    status,
  ]);

  const draft = useMemo(() => {
    return convertDashboardRecordToDraft(dashboardWithCharts);
  }, [dashboardWithCharts]);

  return (
    <ObservationContext.Provider value={observation}>
      <EmbedModeContext.Provider value={true}>
        {!loading && !error && draft && (
          <EmbedDashboardLoadedPageContent
            dashboard={draft}
            dashboardId={dashboardWithCharts.id}
            record={dashboardWithCharts}
          />
        )}
      </EmbedModeContext.Provider>
    </ObservationContext.Provider>
  );
};

// This lets us use hooks inside the EmbedDashboardPage component.
const EmbedDashboardLoadedPageContent = ({
  dashboard,
  dashboardId,
  record,
}: {
  dashboard: DraftDashboard;
  dashboardId: string;
  record: DashboardRecordDto;
}) => {
  const [dashboardState, setDashboardState] =
    useState<DraftDashboard>(dashboard);

  const lastLoadedDashboardId = useRef(dashboardId);
  useEffect(() => {
    if (lastLoadedDashboardId.current !== dashboardId) {
      setDashboardState(dashboard);
      lastLoadedDashboardId.current = dashboardId;
    }
  }, [dashboard, dashboardId]);

  const updateDraftChart: UpdateDraftChartFn = useCallback(
    (chart) => {
      setDashboardState(updateChartInDraft({ draft: dashboard, chart }));
    },
    [dashboard],
  );

  const updateDashboardDraftGlobalFilters: UpdateDashboardDraftGlobalFiltersFn =
    useCallback(
      (globalFilters) => {
        setDashboardState(
          updateGlobalFiltersInDraft({ draft: dashboard, globalFilters }),
        );
      },
      [dashboard],
    );

  const dashboardsHook = useMemo((): DashboardsContext => {
    return {
      isDashboardStale: false,
      updateDraftChart,
      reorderDraftCharts: () => {},
      dashboardDraft: {
        dashboardId: dashboardId,
        draft: dashboardState,
        record,
        dirty: false,
        summaryIntentDirty: false,
        stale: false,
      },
      newDashboardChartList: [],
      fetchDashboardDetails: () => {
        return Promise.resolve(null);
      },
      acknowledgeDashboardStale: () => {},
      initDashboardDraft: () => {},
      addChartToDashboard: () => {
        return Promise.resolve(null);
      },
      dashboardsList: [],
      deleteDashboard: () => {
        return Promise.resolve(null);
      },
      addDraftChart: () => {
        throw new Error('Not implemented in embed mode');
      },
      createEmptyDashboard: () => {
        return Promise.resolve(null);
      },
      duplicateDashboard: () => {
        return Promise.resolve(null);
      },
      fetchDashboardsList: () => {
        return Promise.resolve(null);
      },
      updateDashboard: () => {
        return Promise.resolve(null);
      },
      moveDashboardChart: () => {
        return Promise.resolve(null);
      },
      deleteDraftChart: () => {},
      persistDashboardDraft: () => {
        return Promise.resolve(null);
      },
      persistDashboardDraftState: {
        state: 'idle',
      },
      removeNewDashboardChart: () => {},
      updateDashboardDraftName: () => {},
      updateDashboardDraftDatabaseRevisionId: () => {},
      updateDashboardDraftGlobalFilters,
      markDashboardSummaryIntentDirty: () => {},
      clearDashboardSummaryIntentDirty: () => {},
    };
  }, [
    dashboardId,
    dashboardState,
    record,
    updateDraftChart,
    updateDashboardDraftGlobalFilters,
  ]);

  return (
    <DashboardsContext.Provider value={dashboardsHook}>
      <DashboardDetailsView
        setSelectedNewChartFeature={() => {}}
        setShowNewChartMenu={() => {}}
      />
    </DashboardsContext.Provider>
  );
};
